import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MapPin, Phone, Mail, Youtube, Globe, Shield, FileText, Video, Camera, BookOpen } from 'lucide-react';
import Logo from '@/components/Logo';
import api, { CHURCH, SCHEDULE } from '@/api/client';
import { useSiteSettings } from '@/context/SiteSettingsContext';

export default function Footer() {
  const { settings } = useSiteSettings();
  const showDonate = settings.donate_page_visible !== false;

  // Only surface the Leaflets link when at least one published leaflet exists.
  // Falls back to hidden on error so the footer never shows a dead link.
  const { data: leaflets = [] } = useQuery({
    queryKey: ['pub-leaflets-footer'],
    queryFn: async () => (await api.get('/public/leaflets')).data,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const showLeaflets = Array.isArray(leaflets) && leaflets.length > 0;

  return (
    <footer className="gradient-purple text-white pt-16 pb-8 mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-3">
          <Logo size="lg" light />
          <p className="text-moj-gold/90 text-sm mt-4 italic">“{CHURCH.motto}”</p>
          <p className="text-white/60 text-xs mt-2">{CHURCH.scripture}</p>
          <ul className="mt-5 space-y-2.5 text-sm text-white/90">
            <li className="flex gap-2 items-start"><MapPin size={16} className="mt-0.5 text-moj-gold shrink-0"/> <span>{CHURCH.address}</span></li>
            <li className="flex gap-2 items-center"><Phone size={16} className="text-moj-gold"/> <a href={`tel:${CHURCH.phoneIntl.replace(/\s/g,'')}`} className="hover:underline">{CHURCH.phoneIntl}</a></li>
            <li className="flex gap-2 items-center"><Mail size={16} className="text-moj-gold"/> <a href={`mailto:${CHURCH.emailInfo}`} className="hover:underline">{CHURCH.emailInfo}</a></li>
            <li className="flex gap-2 items-center"><Globe size={16} className="text-moj-gold"/> {CHURCH.website}</li>
          </ul>
        </div>

        <div className="lg:col-span-5">
          <h4 className="text-moj-gold font-serif text-lg mb-4">Weekly Services</h4>
          <ul className="space-y-2 text-sm">
            {SCHEDULE.weekly.map((s) => (
              <li key={s.name} className="flex items-start gap-2">
                <span className="text-moj-gold mt-0.5">✓</span>
                <div className="flex-1">
                  <div className="text-white font-semibold flex items-center gap-2 flex-wrap">
                    {s.name}
                    {s.meet && (
                      <Link to="/morning-prayer" className="inline-flex items-center gap-1 bg-moj-gold/20 hover:bg-moj-gold/30 text-moj-gold text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full transition">
                        <Video size={10}/> Google Meet
                      </Link>
                    )}
                  </div>
                  <div className="text-white/70 text-xs">{s.when} · {s.time}{s.note ? ` · ${s.note}` : ''}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-4">
          <h4 className="text-moj-gold font-serif text-lg mb-4">Monthly Programs</h4>
          <ul className="space-y-2 text-sm">
            {SCHEDULE.monthly.map((s) => (
              <li key={s.name} className="flex items-start gap-2">
                <span className="text-moj-gold mt-0.5">✓</span>
                <div className="flex-1">
                  <div className="text-white font-semibold">{s.name}</div>
                  <div className="text-white/70 text-xs">{s.when}{s.time ? ` · ${s.time}` : ''}</div>
                </div>
              </li>
            ))}
          </ul>

          <h4 className="text-moj-gold font-serif text-lg mt-6 mb-3">Connect</h4>
          <a href={CHURCH.youtube} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white/90 hover:text-moj-gold mb-2">
            <Youtube size={18} className="text-moj-gold"/> YouTube @MinistryofJesus
          </a>
          <Link to="/morning-prayer" className="flex items-center gap-2 text-sm text-white/90 hover:text-moj-gold">
            <Video size={16} className="text-moj-gold"/> Join Morning Prayer (Google Meet)
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 mt-10 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
        <nav className="flex flex-wrap gap-x-5 gap-y-0 text-xs text-white/70" aria-label="Legal links">
          {showDonate && (
            <Link to="/donate" className="hover:text-moj-gold flex items-center gap-1 py-3 touch-manipulation" data-testid="footer-donate-link"><FileText size={12}/> Donate</Link>
          )}
          {showLeaflets && (
            <Link to="/leaflets" className="hover:text-moj-gold flex items-center gap-1 py-3 touch-manipulation" data-testid="footer-leaflets-link"><BookOpen size={12}/> Leaflets</Link>
          )}
          <Link to="/privacy" className="hover:text-moj-gold flex items-center gap-1 py-3 touch-manipulation"><FileText size={12}/> Privacy Notice</Link>
          <Link to="/safeguarding" className="hover:text-moj-gold flex items-center gap-1 py-3 touch-manipulation"><Shield size={12}/> Safeguarding Policy</Link>
          <Link to="/faq" className="hover:text-moj-gold flex items-center gap-1 py-3 touch-manipulation"><Camera size={12}/> Photography &amp; Live Streaming FAQ</Link>
          <Link to="/contact" className="hover:text-moj-gold flex items-center gap-1 py-3 touch-manipulation"><Mail size={12}/> Contact</Link>
        </nav>
        <div className="text-xs text-white/60">© {new Date().getFullYear()} {CHURCH.name} • {CHURCH.tagline} • Registered Charity: RCN 20207036</div>
      </div>
    </footer>
  );
}
